import React, { useEffect, useState } from 'react';
import Layout from '../../components/layout/Layout';

const CALENDLY_URL = 'https://calendly.com/codemyai/30min';

const BookPage: React.FC = () => {
  const [widgetHeight, setWidgetHeight] = useState(700);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://assets.calendly.com/assets/external/widget.css';
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    const handleMessage = (e: MessageEvent) => {
      if (e.data?.event === 'calendly.page_height') {
        const h = parseInt(e.data.payload?.height, 10);
        if (!isNaN(h) && h > 0) setWidgetHeight(h);
      }
    };
    window.addEventListener('message', handleMessage);

    return () => {
      document.head.removeChild(link);
      document.body.removeChild(script);
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  return (
    <Layout>
      {/* Hero */}
      <div className="bg-neu-bg pt-8 pb-6 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <span
            className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase mb-4 text-neu-accent"
            style={{
              boxShadow:
                'inset 3px 3px 6px rgb(163,177,198,0.4), inset -3px -3px 6px rgba(255,255,255,0.6)',
            }}
          >
            Free Consultation
          </span>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold text-neu-text mb-4 leading-tight">
            Book a Free Strategy Call
          </h1>
          <p className="text-neu-muted text-lg max-w-xl mx-auto">
            Pick a time that works for you. We&apos;ll talk through your goals, identify quick wins,
            and outline exactly how AI can grow your business.
          </p>
        </div>
      </div>

      {/* Trust bar */}
      <div className="bg-neu-bg pb-6 px-4">
        <div className="max-w-2xl mx-auto">
          <div
            className="flex flex-wrap items-center justify-center gap-6 rounded-neu-sm px-6 py-4"
            style={{
              boxShadow: '6px 6px 12px rgb(163,177,198,0.5), -6px -6px 12px rgba(255,255,255,0.6)',
            }}
          >
            {[
              { icon: '🕐', text: '30-minute call' },
              { icon: '✅', text: 'No obligation' },
              { icon: '🎯', text: 'Tailored to your business' },
              { icon: '🚀', text: 'Walk away with a clear plan' },
            ].map((item) => (
              <div
                key={item.text}
                className="flex items-center gap-2 text-sm text-neu-muted font-display"
              >
                <span>{item.icon}</span>
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Calendly inline widget */}
      <div className="bg-neu-bg px-4 pb-6">
        <div className="max-w-4xl mx-auto">
          <div
            className="rounded-neu overflow-hidden"
            style={{
              boxShadow: '9px 9px 18px rgb(163,177,198,0.6), -9px -9px 18px rgba(255,255,255,0.5)',
            }}
          >
            <div
              className="calendly-inline-widget"
              data-url={`${CALENDLY_URL}?hide_gdpr_banner=1&primary_color=6366f1`}
              style={{ minWidth: '320px', height: `${widgetHeight}px`, overflow: 'hidden' }}
            />
          </div>

          {/* Fallback nudge */}
          <p className="mt-4 text-center text-sm text-neu-muted font-display">
            Can&apos;t find a suitable time?{' '}
            <a
              href="mailto:Hello@CodeMyAI.com"
              className="text-neu-accent font-semibold hover:underline"
            >
              Email us directly
            </a>{' '}
            or use the{' '}
            <a href="/contact" className="text-neu-accent font-semibold hover:underline">
              contact form
            </a>
            .
          </p>
        </div>
      </div>

      {/* What to expect */}
      <div className="bg-neu-bg px-4 pb-20">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-display text-2xl font-bold text-neu-text text-center mb-8">
            What Happens on the Call
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              {
                step: '01',
                title: 'Understand Your Business',
                desc: 'We listen first — your goals, your pipeline, your biggest bottlenecks.',
              },
              {
                step: '02',
                title: 'Identify Quick Wins',
                desc: 'We pinpoint the 1–2 automation wins that will have the biggest revenue impact immediately.',
              },
              {
                step: '03',
                title: 'Clear Action Plan',
                desc: "You leave with a concrete next step — whether that's a proposal, a pilot, or just a clearer picture.",
              },
            ].map((item) => (
              <div
                key={item.step}
                className="rounded-neu-sm p-6 bg-neu-bg"
                style={{
                  boxShadow:
                    '6px 6px 12px rgb(163,177,198,0.5), -6px -6px 12px rgba(255,255,255,0.6)',
                }}
              >
                <div className="text-3xl font-extrabold text-neu-accent/30 font-display mb-2">
                  {item.step}
                </div>
                <h3 className="font-display font-semibold text-neu-text mb-2">{item.title}</h3>
                <p className="text-sm text-neu-muted">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default BookPage;
